import { BuyListStatus, type BuyListMatchInput } from '~/graphql/graphql';
import { buildFilterTree, type EditableFilterRow } from '~/types/filters';

export const BUY_LIST_FILTER_FIELD = {
  platformProductIds: 'platformProductIds',
  locationId: 'locationId',
  transportVehicleId: 'transportVehicleId',
  status: 'status',
} as const;

export const BUY_LIST_STATUSES: readonly BuyListStatus[] = [
  BuyListStatus.Open,
  BuyListStatus.Fulfilled,
  BuyListStatus.Closed,
];

export type BuyListFilterRow = EditableFilterRow;

const COLLECTION_OPERATORS = ['in', 'notIn'] as const;
const EQUALITY_OPERATORS = ['eq', 'neq'] as const;

export function newBuyListFilterRowId(): string {
  return crypto.randomUUID();
}

export function isListField(field: string): boolean {
  return (
    field === BUY_LIST_FILTER_FIELD.platformProductIds ||
    field === BUY_LIST_FILTER_FIELD.status
  );
}

export function operatorsForField(field: string): readonly string[] | null {
  if (isListField(field)) {
    return COLLECTION_OPERATORS;
  }
  if (
    field === BUY_LIST_FILTER_FIELD.locationId ||
    field === BUY_LIST_FILTER_FIELD.transportVehicleId
  ) {
    return EQUALITY_OPERATORS;
  }
  return null;
}

function isBuyListStatus(value: string): value is BuyListStatus {
  return (BUY_LIST_STATUSES as readonly string[]).includes(value);
}

export function rowToClause(row: BuyListFilterRow): BuyListMatchInput | null {
  const operators = operatorsForField(row.field);
  if (operators === null || !operators.includes(row.operator)) return null;

  if (row.field === BUY_LIST_FILTER_FIELD.platformProductIds) {
    const productIds = (Array.isArray(row.value) ? row.value : []).filter(
      Boolean
    );
    if (productIds.length === 0) return null;
    return {
      platformProductIds:
        row.operator === 'notIn' ? { notIn: productIds } : { in: productIds },
    };
  }

  if (row.field === BUY_LIST_FILTER_FIELD.status) {
    const statuses = (Array.isArray(row.value) ? row.value : []).filter(
      isBuyListStatus
    );
    if (statuses.length === 0) return null;
    return {
      status: row.operator === 'notIn' ? { notIn: statuses } : { in: statuses },
    };
  }

  if (
    row.field === BUY_LIST_FILTER_FIELD.locationId ||
    row.field === BUY_LIST_FILTER_FIELD.transportVehicleId
  ) {
    const selectedId = Array.isArray(row.value) ? '' : row.value;
    if (!selectedId) return null;
    const operatorInput =
      row.operator === 'neq' ? { neq: selectedId } : { eq: selectedId };
    return row.field === BUY_LIST_FILTER_FIELD.locationId
      ? { locationId: operatorInput }
      : { transportVehicleId: operatorInput };
  }

  return null;
}

export function buildBuyListMatchInput(
  rows: BuyListFilterRow[]
): BuyListMatchInput | undefined {
  return buildFilterTree(rows, rowToClause);
}

export function activeRowCount(rows: BuyListFilterRow[]): number {
  return rows.filter((row) => rowToClause(row) !== null).length;
}
